import React, { useState, useContext } from 'react'
import styled from 'styled-components'
import moment from 'moment'
import Input from '../../../../../components/input'
import Button from '../../../../../components/button'
import MoneyIcon from '@material-ui/icons/AttachMoney'
import { AuthContext } from '../../../../../auth-context'
import { createPortalTransaction, loadProfile } from '../../../../../api'
import { DashboardContext } from '../../../dashboard-context'
import { ErrorMessage } from '../../../../../components/error-message'

const Container = styled.div`
  background: #FFF;
	border-radius: 8px;
	box-shadow: 0px 0px 8px 0px #e1e2e6;
  width: 100%;
  padding: 24px;

  @media(max-width: 900px) {
    padding: 16px;
  }
`

const Label = styled.label`
  color: #555;
  display: flex;
  align-items: center;
  font-family: 'DM Sans', sans-serif;
  font-size: 1.25rem;
  font-weight: 500;
  margin-bottom: 24px;

  svg {
    fill: #555;
    height: auto;
    width: 36px;
    margin-right: 16px;
  }

  @media(max-width: 900px) {
    font-size: 1.25rem;
    margin-bottom: 16px;

    svg {
      width: 28px;
    }
  }
`

const Info = styled.div`
  margin-bottom: 24px;

  p {
    color: #5a5a5a;
    font-size: 1rem;
  }

  span {
    font-family: ${props => props.theme.font.primary};
    font-size: 1.25rem;
    font-weight: 500;
  }
`

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 24px;

  @media(max-width: 900px) {
    margin-top: 16px;
  }
`

const AddButton = styled(Button)`
  margin: 0;
  margin-left: 16px;
  min-width: 180px;
`

const Transaction = (props) => {
  const { openSnackbar } = useContext(DashboardContext)
  const { user, updateUser } = useContext(AuthContext)
  const [amount, updateAmount] = useState(user.balance.toFixed(2))
  const [error, updateError] = useState(null)
  const [submitting, updateSubmitting] = useState(false)

  const handleSubmit = (e) => {
    let newError = null
    let validatedAmount = null
    let numericAmount = amount.trim()
    if (numericAmount.indexOf('$') === 0) {
      numericAmount = numericAmount.slice(1)
    }
    if (numericAmount === '') {
      newError = 'Please enter the amount of the payment.'
    } else {
      validatedAmount = parseFloat(numericAmount)
      if (isNaN(validatedAmount)) {
        newError = 'The amount of the payment must be numeric.'
      } else if (validatedAmount <= 0) {
        newError = 'The amount of the payment must be greater than zero.'
      } else if (validatedAmount > user.balance) {
        newError = `The amount of the payment must be less than ${user.balance}.`
      }
    }
    if (newError !== null) {
      updateError(newError)
    } else {
      updateError(null)
      updateSubmitting(true)
      createPortalTransaction(user.id, validatedAmount).then((transactionResult) => {
        loadProfile(user.id).then((res) => {
          if (res.data.profile.active === 1) {
            updateUser(res.data.profile)
          }
        }).catch((err) => {
          console.log(err)
        })
        if (transactionResult.data.success) {
          switch (transactionResult.data.transaction.status) {
            case 'submitted_for_settlement':
            case 'settling':
            case 'settled':
              openSnackbar('The payment was successfully made.', false)
              break

            default:
              let reason = transactionResult.data.transaction.status
              if (transactionResult.data.transaction.processorResponseText) {
                reason = reason + ', ' + transactionResult.data.transaction.processorResponseText
              }
              openSnackbar('The payment failed: ' + reason, true)
          }
        } else {
          openSnackbar('The payment could not be made: ' + transactionResult.data.error, true)
        }
        updateSubmitting(false)
      }).catch((err) => {
        openSnackbar('There was a problem making the payment.', true)
        updateSubmitting(false)
      })
    }
  }
  
  return (
    <React.Fragment>
      <Container className={props.className}>
        <Label>
          <MoneyIcon /> Make Payment
        </Label>
        <Info>
          <p>You have an outstanding balance of <span>${user.balance.toFixed(2)}</span>. After you update your credit card as needed, you can make a payment here.</p>
        </Info>
        <Info>
          <p>Your payment on <span>{moment.utc(user.payment_alert_date).format('MMM D, YYYY')}</span> failed with status <span>{user.payment_alert_status + (user.payment_alert_details ? (', ' + user.payment_alert_details) : '')}</span>. {user.payment_alert_code === '2004' ? 'Your credit card has expired. Please enter your new card number and expiration date under Billing Information below, if you have not already done so.' : 'In most cases, you should contact your bank to find out the specific reason.'}</p>
        </Info>
        <Input
          error={error !== null}
          label="Amount to be paid"
          value={amount}
          onChange={updateAmount}
        />
        {error !== null && 
          <ErrorMessage>{error}</ErrorMessage>
        }
        <ButtonContainer>
          <AddButton 
            label="Make payment"
            isLoading={submitting}
            onClick={handleSubmit}
          />
        </ButtonContainer>
      </Container>
  </React.Fragment>
  )
}

export default Transaction